import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Skeleton,
  Avatar,
  Tooltip,
  Box,
  Button,
  Grid
} from "@mui/material";
import { DataGridPro, gridDateComparator } from "@mui/x-data-grid-pro";

import { selectAllBuyRequests, selectAllEvents, selectDeliveryMethods } from "../../../../../../store/slices/swarms";
import { useLazyGetBuyRequestQuery } from "../../../../../../services/swarmsApi";
import { ApplicationTable, BuyRequestStatus } from "../../../../../../constants/enums";
import { useGetUserStyleOrDefaultQuery, useUpdateColumnsForUserMutation } from "../../../../../../services/tableStyleApi";
import { BUY_REQUEST_GRID_COLUMN } from "../../../../../AdminSwarm/PendingBuyRequestGridColumn";
import useDidMountEffect from "../../../../../../customHooks/useDidMountEffect";
import VenueMapDialog from "../../../../../../screens/AdminSwarm/VenueMapDialog/VenueMapDialog";
import { selectAllBuyManagers } from "../../../../../../store/slices/buyManager/buyManagers";
import ActionButtonsCell from "./ActionButtonsCell";
import WaitingCell from "./WaitingCell";
import PendingBuyRequestsHeader from "./PendingBuyRequestsHeader";
import {selectSwarmSelected, selectAllSwarms} from "../../../../../../store/slices/swarmsInAction";
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PassApproveAllButtons from "./PassApproveAllButtons";
import { useDeliveryMethodsQuery } from "../../../../../../services/jobsLookUpTables";
import { useLazyGetEventsQuery } from "@/services/eventApi";
import Notification from "@/components/Notification";

const PendingBuyRequests = () => {
  const [showError, setShowError] = useState({ open: false, text: '' })
  const buyRequests = useSelector(selectAllBuyRequests);
  const {data: deliveryMethods, isLoading: deliveryMethodsLoading} = useDeliveryMethodsQuery();
  const [getBuyRequests] = useLazyGetBuyRequestQuery();
  const swarmSelected= useSelector(selectSwarmSelected);
  const swarmsInAction= useSelector(selectAllSwarms);
  
  const [pendingBuyRequestGrid, setPendingBuyRequestGrid] = useState([]);

  const [columns, setColumns] = useState([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({})

  const { data: styles } = useGetUserStyleOrDefaultQuery(ApplicationTable.PendingBuyRequest);
  const [updateColumnsForUser] = useUpdateColumnsForUserMutation();
  const [gridStyle, setGridStyle] = useState(null);
  const [isFirstGridLoad, setIsFirstGridLoad] = useState(true);
  const [isFirstGridLoadBuyRequest, setIsFirstGridLoadBuyRequest] = useState(true);

  const [isVenueMapOpened, setIsVenueMapOpened] = useState(false);
  const [venueMapData, setVenueMapData] = useState(null);
  const buyManagers = useSelector(selectAllBuyManagers);

  const events = useSelector(selectAllEvents);
  const [getEvents, {}] = useLazyGetEventsQuery();

  // Helper function to fetch missing events information
  const fetchMissingEvents = (eventsIds) => {
    console.log('[fetchMissingEvents] Input events IDs:', eventsIds);
    // Delete duplicated events ids
    const uniqueEventsIds = [...new Set(eventsIds)];
    console.log('[fetchMissingEvents] Unique events IDs:', uniqueEventsIds);
    // Delete events that already exists in redux selector
    const eventsIdsToFetch = uniqueEventsIds.filter(bteId => !(events || []).some(e => e.bteId === bteId));
    console.log('[fetchMissingEvents] Events IDs to fetch:', eventsIdsToFetch);
    if(eventsIdsToFetch.length === 0) return;
    // Build the query string for the request
    const query = eventsIdsToFetch.map(bteId => `bteId=${bteId}`).join('&');
    console.log('[fetchMissingEvents] API query string:', query);
    // Make the request
    getEvents(`?${query}`);
  };

  const onShowVenueMapClick = (exchangeId, seatsData) => {
    setVenueMapData({
      eventId: exchangeId,
      seatsData: [seatsData]
    })
    setIsVenueMapOpened(true)
  }

  const gridColumns = useMemo(() => ({
    [BUY_REQUEST_GRID_COLUMN.ID]: { 
      headerName: "ID", 
      field: 'id' 
    },
    [BUY_REQUEST_GRID_COLUMN.QUANTITY]: { 
      headerName: "#", 
      field: "quantity", 
      flex: 0.25, 
      headerAlign: 'center', 
      align: 'center' 
    },
    [BUY_REQUEST_GRID_COLUMN.SECTION]: { 
      headerName: "SEC", 
      field: "section", 
      flex: 0.25, 
      headerAlign: 'center', 
      align: 'center',
      renderCell: (params) => renderSectionColumn(params, onShowVenueMapClick)
    },
    [BUY_REQUEST_GRID_COLUMN.ROW]: { 
      headerName: "ROW", 
      field: "row", 
      flex: 0.25, 
      headerAlign: 'center', 
      align: 'center' 
    },
    [BUY_REQUEST_GRID_COLUMN.SEATS]: { 
      headerName: "SEATS", 
      field: "seats", 
      flex: 0.5 
    },
    [BUY_REQUEST_GRID_COLUMN.DELIVERY_METHOD]: { 
      headerName: "DELIVERY", 
      field: "delivery", 
      flex: 1, 
      valueGetter: v => getDeliveryMethodName(v.row.deliveryMethodID) 
    },
    [BUY_REQUEST_GRID_COLUMN.NTL]: { 
      headerName: "", 
      field: "ntl", 
      flex:0.1,
      renderCell: params => params.row.isNonTransferrableLanguage && <div className="non-transferrable-language">NTL</div> 
    }, 
    [BUY_REQUEST_GRID_COLUMN.COST_PER_TICKETS]: { 
      headerName: "CPT", 
      field: "reward", 
      flex: 0.50, 
      headerAlign: 'center', 
      align: 'center', 
      valueGetter: v => v?.row?.totalCost / v.row.quantity, 
      renderCell: v => '$ ' + v.value.toFixed(2) 
    },
    [BUY_REQUEST_GRID_COLUMN.WAITING]: { 
      headerName: "WAITING", 
      renderHeader: () => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Additional information about this column">
            <TimerOutlinedIcon style={{ fontSize: '12px', marginLeft: '0.5em', cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      field: "waiting", 
      flex: 0.50, 
      headerAlign: 'center', 
      align: 'center', 
      sortComparator: (a, b) => gridDateComparator(b, a), 
      valueGetter: v => v.row.createdOn, 
      renderCell: (v) => <WaitingCell ts={(new Date(v.value.toLowerCase().endsWith('z') ? v.value : v.value + 'Z')).valueOf()}/> 
    },
    [BUY_REQUEST_GRID_COLUMN.MANAGE]: { 
      headerName: "", 
      field: "buy", 
      resizable: false, 
      filterable: false, 
      hideSortIcons: true, 
      sortable: false, 
      flex: 1.6,
      renderCell: (params) => <ActionButtonsCell props={params} setShowError={setShowError} /> 
    },
  }), [onShowVenueMapClick, getDeliveryMethodName, setShowError]);

  const getSavedGrid = () => {
    return sessionStorage.getItem("PENDING_BUY_REQUEST_GRID") ? JSON.parse(sessionStorage.getItem("PENDING_BUY_REQUEST_GRID")) : []
  }

  useEffect(() => {
    const fetchBuyRequests = async () => {
      const resTypes = [BuyRequestStatus.Pending, BuyRequestStatus.Accepted];
      try {
        await getBuyRequests({ SwarmResolutionTypeId: resTypes }).unwrap();
      } catch (error) {
        if (error.status === 401) return;
        throw error;
      } finally {
        setIsFirstGridLoadBuyRequest(false);
      }
    };
  
    fetchBuyRequests();
  }, [getBuyRequests]);

  const rows = useMemo(() => {
    if (!pendingBuyRequestGrid) return [];

    //A in the pendingBuyRequestGrid we have the bteId, but we have the swarmId
    //A: then we can find the swarm in the swarmsInAction slice and get the exchangeId
    //A: then we can find the event in the events slice and get the exchangeId
    let mappedRows = pendingBuyRequestGrid.map(br =>{
      const swarm = swarmsInAction?.find(swarm => swarm?.id === br?.swarmId);
      const event= events?.find(event => event?.bteId === swarm?.bteId);
      return {
        ...br,
        exchangeId: event?.exchangeId
      };
    });
    
    //A: when the swarm 2.0 ui load, we get all in-action swarms, and buyManagers.
    //A: here we receive the list of swarms, filtered by the selected buyManager or not
    //A: if not swarm selected, can be: 
    //1- a buyManager is selected and there aren't any swarm -> show nothing
    //2- a buyManager is selected and there is a swarm -> show the swarms filtered by swarmID
    if(!swarmSelected){
      mappedRows = []
    }else if(swarmSelected){
      mappedRows = mappedRows.filter(row => row.swarmId === swarmSelected.id);
    }
      
    return mappedRows.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
  }, [pendingBuyRequestGrid, buyManagers, swarmSelected, events]);

  useDidMountEffect(() => {
    const brIds = pendingBuyRequestGrid.map(br => br.id);
    sessionStorage.setItem("PENDING_BUY_REQUEST_GRID", JSON.stringify(brIds))
  }, [pendingBuyRequestGrid])

  useEffect(async () => {
    if (!buyRequests || isFirstGridLoadBuyRequest) 
      return;

    const buyRequestIds = getSavedGrid().filter(brId => !buyRequests.some(r => r.id === brId));

    if (buyRequestIds.length !== 0)
    {
      try {
        await getBuyRequests({BuyRequestId: buyRequestIds, extraOptions: true})
      } catch (error) {
        if (error.status == 401) return;
        throw error;
      }
    }

    const updatedGrid = getSavedGrid().map(brId => buyRequests.find(x => x.id === brId)).filter(x => !!x);

    const newPendingRows = buyRequests.filter(
      (br) => br.swarmResolutionTypeId === BuyRequestStatus.Pending && !getSavedGrid().some((rId) => rId === br.id)
    );

    const totalBuyRequest= [...updatedGrid, ...newPendingRows];

    const eventsIds = totalBuyRequest.map(br => br.bteId);
    fetchMissingEvents(eventsIds);

    setPendingBuyRequestGrid(totalBuyRequest);
  }, [buyRequests, isFirstGridLoadBuyRequest])

  useEffect(() => {
    if (!styles)
      return;

    setGridStyle([...styles]);
  }, [styles])

  useEffect(async () => {
    if (!styles || !gridStyle)
      return;

    let displayColumns = [];
    let hideColumns = {};

    const sortedColumns = [...gridStyle].sort((a, b) => a.index - b.index);
    sortedColumns.forEach(column => {
      let displayColumn = gridColumns[column.name];
      
      if(!displayColumn){
        //TODO: actually we are using this table in two places, one the old and the other here (adminSwarm2.0)
        //A: the oldest version has more columns, and here we are using less columns, for that reason it produces errors when not founding the columns
        //A: in the future when the old version is removed, this error will be resolved
        // console.error("Column Not Found, column: ", column, " gridColumns: ", gridColumns);
      } else {        
        if (column.width) {
          displayColumn.flex = 0;
          displayColumn.width = column.width;
        }else{
          displayColumn.flex = displayColumn.flex || 1;
          delete displayColumn.width;
        }

        hideColumns[displayColumn.field] = column.mustBeVisible;
        displayColumns.push(displayColumn);
      }
    });

    setColumnVisibilityModel(hideColumns);
    setColumns(displayColumns);

    if (!isFirstGridLoad) {
      await updateColumnsForUser({ table: ApplicationTable.PendingBuyRequest, columns: gridStyle }).unwrap();
    }
    setIsFirstGridLoad(false);
  }, [gridStyle])

  const onCloseVenueMapClick = () => {
    setIsVenueMapOpened(false)
  }

  const onRefreshClick = () => {
    const resTypes = [BuyRequestStatus.Pending, BuyRequestStatus.Accepted];

    setPendingBuyRequestGrid([])
    try {
      getBuyRequests({ SwarmResolutionTypeId: resTypes }).unwrap()
    } catch (error) {
      if (error.status == 401) return;
      throw error;
    }
  }

  function getDeliveryMethodName(deliveryMethodId) {
    const dm = deliveryMethods?.find(dm => dm.id == deliveryMethodId);
    return dm?.name || 'N/A';
  }

  const onColumnVisibilityModelChange = (newModel) => {
    if (Object.keys(newModel).length === 0)
      Object.entries(gridColumns).forEach(([key, value]) => newModel[value.field] = true)

    let newStyle = gridStyle.map(style => {
      let gridRow = gridColumns[style.name]

      const [key, value] = Object.entries(newModel).find(([key, column]) => key === gridRow.field) || [];

      return {
        ...style,
        mustBeVisible: key ? value : style.mustBeVisible
      }
    });

    setGridStyle(newStyle);
  }

  const onColumnWidthChanged = ({ colDef, element, width }) => {
    const [key] = Object.entries(gridColumns).find(([key, column]) => column.field === colDef.field)

    let newStyle = gridStyle.map(style => {
      const isSelectedStyle = style.name === key

      return {
        ...style,
        width: isSelectedStyle ? Math.round(width) : style.width
      }
    });

    setGridStyle(newStyle);
  }

  const onColumnOrderChange = ({ colDef, element, field, oldIndex, targetIndex }) => {
    const [key] = Object.entries(gridColumns).find(([key, column]) => column.field === field)

    ++targetIndex; ++oldIndex;
    let newStyle = gridStyle.map(style => {
      const isSelectedStyle = style.name === key;
      const isPrevColumn = style.index === targetIndex;

      return {
        ...style,
        index: isSelectedStyle ? targetIndex : isPrevColumn ? oldIndex : style.index
      }
    });

    setGridStyle(newStyle);
  }

  function renderSectionColumn(params) {
    if (!params.value)
      return <Skeleton variant="text" width="70%" />

    return (
      <Box onClick={() => 
        params.row.exchangeId ? 
          onShowVenueMapClick(
            params.row.exchangeId, 
            {
              tgID: params.row.id,
              tgUserSec: params.row.section,
              tgUserRow: params.row.row,
              tgUserSeats: params.row.seats,
              tgQty: params.row.quantity,
              tgPrice: params.row.reward,
              tgNotes: ''
            }) 
          : null
        } 
        className={params.row.exchangeId ? "clickable-block" : ""}
      >
        {params.value}
      </Box>
    )
  }

  /**
   * Determines the class name for a row based on the buy request status and view limitations.
   * 
   * @param {Object} params - The parameters containing row data.
   * @param {Object} params.row - The row object with relevant properties.
   * @param {number} params.row.swarmResolutionTypeId - The status ID of the swarm resolution.
   * @param {boolean} params.row.isLimitedView - Indicates if the view is limited.
   * 
   * @returns {string} - Returns a class name based on the conditions:
   *                     - 'data-grid-not-active-row' if not pending.
   *                     - 'pending-buy-request-limited-row' if pending and limited view.
   *                     - An empty string otherwise.
   */
  function getRowClassNameHandler(params){
    return params.row.swarmResolutionTypeId !== BuyRequestStatus.Pending
      ? 'data-grid-not-active-row'
      : params.row.isLimitedView
        ? 'pending-buy-request-limited-row'
      : '';
  }

  return (
    <Fragment>
      
      <PendingBuyRequestsHeader onRefreshClick={onRefreshClick} />
      <PassApproveAllButtons 
        swarmSelected={swarmSelected} 
        hasPendingBuyRequest={rows.some(row => row.swarmResolutionTypeId == BuyRequestStatus.Pending)}
      />

      <Notification // on buy click validations error
        snackBarOpenProp={showError.open}
        handleClose={() => setShowError({ open: false, text: '' })}
        text={showError.text}
        status="Error"
      />

      <VenueMapDialog open={isVenueMapOpened} handleClose={onCloseVenueMapClick} data={venueMapData}/>

      <DataGridPro
        headerHeight={40}
        rowHeight={36}
        hideFooter
        sx={{
          height: "65vh",
          minWidth:"580px",
          borderRadius: "1em",
          backgroundColor: "white",
          padding: "0",
          '&:disabled': { backgroundColor: "red" },
          margin: '0 0 1em 0'
        }}
        getRowClassName={getRowClassNameHandler}
        isRowSelectable={() => false}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => onColumnVisibilityModelChange(newModel)}
        onColumnWidthChange={onColumnWidthChanged}
        onColumnOrderChange={onColumnOrderChange}
        rows={rows}
        hideFooterSelectedRowCount
        checkboxSelection={false}
        disableColumnPinning={true}
      />
    </Fragment>
  );
}

export default PendingBuyRequests;
